import { Box, Grid, Skeleton, Typography } from "@mui/material";
import { Contest, ProvincialDistrictTabulation, User } from "../../types";
import { useEffect, useState } from "react";

import CandidateComparison from "./CandidateComparison";
import { fetchProvincialCandidates } from "../../utils/candidates";
import useAuth from "../../hooks/useAuth";
import { useNavigate } from "react-router";

interface ProvincialDistrictDashProps {
  tabulation: ProvincialDistrictTabulation;
  navToStats: boolean;
  showAll?: boolean;
  onComplete?: () => void;
}

const ProvincialDistrictDash: React.FC<ProvincialDistrictDashProps> = ({ 
  tabulation, 
  navToStats, 
  showAll = true,
  onComplete
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [contests, setContests] = useState<Contest[]>([]);
  const [currentContestIndex, setCurrentContestIndex] = useState(0);

  const fetchContests = async (tabulation: ProvincialDistrictTabulation, user: User) => {
    const provincialContests = await fetchProvincialCandidates(tabulation.province);
    if (user.allowedContests) {
      const filteredContets = provincialContests.filter((contest) => user.allowedContests!.includes(contest.position));
      setContests(filteredContets);
    } else {
      setContests(provincialContests);
    }
  };

  useEffect(() => {
    if (user) {
      setContests([]);
      setCurrentContestIndex(0);
      fetchContests(tabulation, user);
    }
  }, [tabulation, user]);

  useEffect(() => {
    if (!showAll && contests.length > 0) {
      const interval = setInterval(() => {
        setCurrentContestIndex((prevIndex) => {
          const newIndex = (prevIndex + 1) % contests.length;
          // If we've cycled back to the first contest, consider it complete
          if (newIndex === 0 && onComplete) {
            onComplete();
          }
          return newIndex;
        });
      }, 10000); // 10 seconds

      return () => clearInterval(interval);
    }
  }, [contests, showAll, onComplete]);

  useEffect(() => {
    if (showAll && contests.length > 0 && onComplete) {
      onComplete();
    }
  }, [showAll, contests.length, onComplete]);

  const onClick = (position: string) => {
    navigate(`/dashboard/candidateStats?type=${tabulation.type}&position=${position}&region=${tabulation.region}&province=${tabulation.province}&district=${tabulation.district}`);
  };

  const renderContests = () => {
    if (contests.length === 0) {
      return Array.from(new Array(3)).map((_, index) => (
        <Grid item xs={12} key={`skeleton-${index}`}>
          <Skeleton variant="rectangular" height={100} sx={{ my: 1 }} />
        </Grid>
      ));
    }

    if (showAll) {
      return contests.map((contest, index) => (
        <Grid item xs={12} key={`${contest.position}-${index}`}>
          <CandidateComparison
            tabulation={tabulation}
            title={contest.position}
            position={contest.position}
            candidates={contest.candidates}
            onClick={navToStats ? () => onClick(contest.position) : undefined}
          />
        </Grid>
      ));
    }

    // Carousel mode
    const currentContest = contests[currentContestIndex];
    return (
      <Grid item xs={12}>
        <CandidateComparison
          tabulation={tabulation}
          title={currentContest.position}
          position={currentContest.position}
          candidates={currentContest.candidates}
          onClick={navToStats ? () => onClick(currentContest.position) : undefined}
        />
      </Grid>
    );
  };

  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      <Typography variant="h4" gutterBottom align="center">
        {tabulation.province} {tabulation.district}
      </Typography>
      <Typography variant="subtitle1" gutterBottom align="center" color="text.secondary">
        Total Voters: {tabulation.totalVoters}
      </Typography>
      <Grid container spacing={1}>
        {renderContests()}
      </Grid>
      {showAll ? null : (
        <Box mt={2} display="flex" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">
            Showing contest {currentContestIndex + 1} of {contests.length}
          </Typography>
        </Box>
      )}
      <Typography mt={3} gutterBottom align="right" color="text.secondary">
        Last Updated: {tabulation.updatedAt.toDate().toLocaleString()}
      </Typography>
    </Box>
  );
};

export default ProvincialDistrictDash;
